import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Build timestamp injected at compile time (see craco.config.js). Used as a
// cache-busting query param so a new build invalidates cached media. Falls back
// to "dev" when running outside a built bundle.
export const BUILD_TS = process.env.REACT_APP_BUILD_TS || "dev";

// Append `?v=<build timestamp>` to a media URL so caches refresh on each build.
// Works for external/absolute URLs (e.g. media.sec4us.com.br) and preserves any
// existing query string and #hash fragment.
export function withVersion(url) {
  if (!url) return url;
  const [beforeHash, hash] = String(url).split("#");
  const sep = beforeHash.includes("?") ? "&" : "?";
  return `${beforeHash}${sep}v=${BUILD_TS}${hash ? `#${hash}` : ""}`;
}

// Human byte size with binary scaling: "0 B", "812 KB", "4.7 GB".
const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB"];
export function formatBytes(bytes, lang) {
  const n = Number(bytes) || 0;
  if (n <= 0) return `0 ${BYTE_UNITS[0]}`;
  let value = n;
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const digits = unit <= 1 ? 0 : 1;
  const num = value.toLocaleString(lang || undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${num} ${BYTE_UNITS[unit]}`;
}

// Human bitrate with decimal (1000) scaling: "812 Kbps", "2.5 Mbps".
const BPS_UNITS = ["bps", "Kbps", "Mbps", "Gbps"];
export function formatBps(bps, lang) {
  const n = Number(bps) || 0;
  if (n <= 0) return `0 ${BPS_UNITS[0]}`;
  let value = n;
  let unit = 0;
  while (value >= 1000 && unit < BPS_UNITS.length - 1) {
    value /= 1000;
    unit += 1;
  }
  const digits = unit === 0 ? 0 : 1;
  const num = value.toLocaleString(lang || undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${num} ${BPS_UNITS[unit]}`;
}

// Compact number: 12_400 -> "12.4k". Used for animated impact counters.
export function formatCompact(n, lang) {
  return Number(n || 0).toLocaleString(lang || undefined, {
    notation: "compact",
    maximumFractionDigits: 1,
  });
}

// Short clock like "01:23:45" from seconds.
export function formatClock(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (x) => String(x).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}
